import './index.scss';

import { useEffect, useMemo, useRef, useState } from 'react';
import { Popconfirm, Input, Button } from 'antd';
import debounce from 'lodash.debounce';
import { PlusOutlined, CloseOutlined } from '@ant-design/icons';

import { splitNumberWithCommas } from '../../utils/general';
import CreditUpdateComponent from '../CreditUpdateComponent';

const { Search } = Input;

const useDebounce = (callback: () => void) => {
  const ref = useRef<(() => void) | null>(null);

  useEffect(() => {
    ref.current = callback;
  }, [callback]);

  /* eslint-disable react-hooks/refs -- ref.current is accessed in the debounced callback, not during render */
  const debouncedCallback = useMemo(() => {
    const func = () => {
      ref.current?.();
    };

    return debounce(func, 500);
  }, []);
  /* eslint-enable react-hooks/refs */

  return debouncedCallback;
};

const TopSection = (props) => {
  const {
    count = null,
    placeholder = '',
    showNewButton = true,
    showExportCsv = false,
    showSearch,
    selectedRowKeys,
    handleSearchQuery,
    handleFormReset,
    renderExportComponent,
    toggleCreateForm,
    handleDeleteMultiple,
    deleteButtonText = 'Delete',
    searchValue = '',
    renderUpdateCredits = false,
    onCreditsUpdate,
    searchPrefix,
  } = props;
  const [query, setQuery] = useState(searchValue);
  const [prevSearchValue, setPrevSearchValue] = useState(searchValue);
  if (prevSearchValue !== searchValue) {
    setPrevSearchValue(searchValue);
    setQuery(searchValue);
  }

  const debouncedSearch = useDebounce(() => {
    handleSearchQuery(query);
  });

  const onClearFilters = () => {
    handleFormReset();
    setQuery('');
  };

  return (
    <div className="table-header">
      <div style={{ display: 'inline-flex', gap: '20px' }}>
        {showNewButton && (
          <div>
            <Button
              icon={<PlusOutlined />}
              type="primary"
              onClick={() => {
                toggleCreateForm(true);
              }}
            >
              New
            </Button>
          </div>
        )}
        {showExportCsv && <div>{renderExportComponent()}</div>}
        {renderUpdateCredits && (
          <CreditUpdateComponent onCreditsUpdate={onCreditsUpdate} />
        )}
        {selectedRowKeys && selectedRowKeys.length > 0 && (
          <Popconfirm
            title={`${deleteButtonText}?`}
            placement="rightBottom"
            onConfirm={handleDeleteMultiple}
          >
            <Button type="primary">{deleteButtonText}</Button>
          </Popconfirm>
        )}
      </div>
      {showSearch && (
        <>
          <div
            style={{
              marginLeft: 'auto',
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
            }}
          >
            {searchPrefix && <div>{searchPrefix}</div>}

            <div style={{ width: '450px' }}>
              <Search
                placeholder={placeholder}
                autoComplete="__away"
                onChange={(e) => {
                  debouncedSearch();
                  setQuery(e.target.value);
                }}
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === 'Enter')
                    handleSearchQuery(e.currentTarget.value);
                }}
                enterButton
                value={query}
              />
            </div>
          </div>
          <div style={{ marginRight: 'auto' }}>
            <Button
              icon={<CloseOutlined />}
              onClick={onClearFilters}
              className="clear"
            >
              Clear Filters
            </Button>
          </div>
        </>
      )}
      {count !== null && (
        <div style={{ color: 'var(--color-primary)', textAlign: 'end' }}>
          {splitNumberWithCommas(count)} {count > 1 ? 'Results' : 'Result'}
        </div>
      )}
    </div>
  );
};

export default TopSection;
